// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/CargaForm.tsx
//  Formulário de Carga — Iniciar / Detalhe / Retorno
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Truck, PackageCheck,
  CheckCircle2, RotateCcw, MapPin, Check, ChevronsUpDown, Loader2,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { cargaService }                  from '@/features/operacional/services/cargaService';
import { calcularStatusCarga }           from '@/features/operacional/types';
import { apiClient, getApiErrorMessage } from '@/lib/apiClient';
import { useVeiculosCombo }              from '@/features/veiculos/hooks/useVeiculos';
import { useRotaCombo }                  from '@/features/rotas/hooks/useRotas';
import { cn }                            from '@/lib/utils';

import { Button }               from '@/components/ui/button';
import { Input }                from '@/components/ui/input';
import { Label }                from '@/components/ui/label';
import { Badge }                from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator }            from '@/components/ui/separator';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';

import type {
  IniciarCargaRequest,
  CargaProdutoRequest,
  RetornarCargaRequest,
  CargaProduto,
  AtualizarCargaRequest,
} from '@/features/operacional/types';

const NONE = '__none__';

const brl = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ── Seletor de produto (busca + auto-preço, espelha o frm_consulta_produto) ──

interface ProdutoBusca {
  id:          number;
  descricao:   string;
  codigoBarras: string | null;
  precoPadrao: number;
}

interface ProdutoPickerProps {
  value?:    number;
  descricao?: string;
  onPick:    (p: ProdutoBusca) => void;
}

function ProdutoCargaPicker({ value, descricao, onPick }: ProdutoPickerProps) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');

  const { data: results = [], isLoading } = useQuery({
    queryKey: ['produto-busca-carga', search],
    queryFn:  async () => {
      const r = await apiClient.get<ProdutoBusca[]>('/api/produtos/buscar', {
        params: { termo: search, limite: 30 },
      });
      return r.data;
    },
    enabled:   open && search.trim().length >= 2,
    staleTime: 10_000,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" type="button"
          className="w-full justify-between font-normal h-9">
          <span className="truncate">
            {value
              ? (descricao ? `[${value}] ${descricao}` : `Produto #${value}`)
              : 'Selecionar produto...'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Digite para buscar produto..."
            value={search} onValueChange={setSearch} />
          <CommandList>
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isLoading && (
              <CommandEmpty>
                {search.trim().length < 2
                  ? 'Digite ao menos 2 caracteres...'
                  : 'Nenhum produto encontrado.'}
              </CommandEmpty>
            )}
            <CommandGroup>
              {results.map(p => (
                <CommandItem key={p.id} value={String(p.id)}
                  onSelect={() => { onPick(p); setOpen(false); }}>
                  <Check className={cn('mr-2 h-4 w-4 shrink-0', value === p.id ? 'opacity-100' : 'opacity-0')} />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{p.descricao}</span>
                    <span className="text-xs text-muted-foreground">
                      [{p.id}]{p.codigoBarras ? ` · ${p.codigoBarras}` : ''} · {brl(p.precoPadrao)}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ── NovaCargaForm ─────────────────────────────────────────────────────────

type LinhaProduto = CargaProdutoRequest & { _key: number; descricao?: string };

function NovaCargaForm() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();

  const { data: veiculos = [] } = useVeiculosCombo();
  const { rotas }               = useRotaCombo();

  const [dtMontagem, setDtMontagem] = useState(() => new Date().toISOString().slice(0, 10));
  const [motorista,  setMotorista]  = useState('');
  const [veiculoId,  setVeiculoId]  = useState('');
  const [rotaId,     setRotaId]     = useState('');

  const [produtos, setProdutos] = useState<LinhaProduto[]>([]);
  const [_nextKey, setNextKey]  = useState(1);

  const addLinha = () => {
    setNextKey((k) => {
      setProdutos((p) => [...p, { _key: k, produtoId: 0, subProdutoId: 0, quantidade: 1, valorUnitario: 0 }]);
      return k + 1;
    });
  };

  const removeLinha = (key: number) => setProdutos((p) => p.filter((r) => r._key !== key));

  // Produto via busca → preenche código, descrição e (se vazio) o preço, como no desktop.
  const pickProduto = (key: number, p: ProdutoBusca) =>
    setProdutos((prev) => prev.map((r) =>
      r._key === key
        ? { ...r, produtoId: p.id, subProdutoId: p.id, descricao: p.descricao, valorUnitario: r.valorUnitario || p.precoPadrao }
        : r));

  const updateNumero = (key: number, field: 'quantidade' | 'valorUnitario', value: string) =>
    setProdutos((p) => p.map((r) => r._key === key ? { ...r, [field]: Number(value) } : r));

  const saveMutation = useMutation({
    mutationFn: () => {
      if (produtos.length === 0) throw new Error('Adicione ao menos um produto');
      if (produtos.some((p) => !p.produtoId)) throw new Error('Selecione o produto em todas as linhas');
      const request: IniciarCargaRequest = {
        dtMontagem: new Date(dtMontagem).toISOString(),
        produtos: produtos.map(({ produtoId, subProdutoId, quantidade, valorUnitario }) => ({
          produtoId, subProdutoId, quantidade, valorUnitario,
        })),
        motorista: motorista || undefined,
        veiculoId: veiculoId ? Number(veiculoId) : undefined,
        rotaId:    rotaId    ? Number(rotaId)    : undefined,
      };
      return cargaService.iniciar(request);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['carga'] });
      toast.success('Carga iniciada com sucesso');
      navigate(`/operacional/cargas/${data.id}`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const totalCarga = produtos.reduce((s, p) => s + p.quantidade * p.valorUnitario, 0);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/cargas')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">Nova Carga</h1>
      </div>

      {/* Cabeçalho */}
      <Card>
        <CardHeader><CardTitle className="text-base">Identificação</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label>Data de Montagem</Label>
            <Input type="date" value={dtMontagem} onChange={(e) => setDtMontagem(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Motorista</Label>
            <Input value={motorista} onChange={(e) => setMotorista(e.target.value)} placeholder="Nome do motorista" />
          </div>
          <div className="space-y-2">
            <Label>Veículo</Label>
            <Select value={veiculoId || NONE} onValueChange={(v) => setVeiculoId(v === NONE ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Selecione o veículo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— Nenhum —</SelectItem>
                {veiculos.map((v) => (
                  <SelectItem key={v.id} value={String(v.id)}>
                    {(v.placa ?? `Veículo ${v.id}`)}{v.modelo ? ` — ${v.modelo}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Rota</Label>
            <Select value={rotaId || NONE} onValueChange={(v) => setRotaId(v === NONE ? '' : v)}>
              <SelectTrigger><SelectValue placeholder="Selecione a rota" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>— Nenhuma —</SelectItem>
                {rotas.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>{r.descricao}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Produtos */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Produtos da Carga</CardTitle>
            <Button size="sm" variant="outline" className="gap-2" onClick={addLinha}>
              <Plus className="h-4 w-4" />Adicionar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {produtos.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <PackageCheck className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p>Nenhum produto adicionado</p>
              <Button variant="outline" className="mt-3 gap-2" onClick={addLinha}>
                <Plus className="h-4 w-4" />Adicionar produto
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[260px]">Produto</TableHead>
                  <TableHead className="text-right">Quantidade</TableHead>
                  <TableHead className="text-right">Valor Unit.</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {produtos.map((p) => (
                  <TableRow key={p._key}>
                    <TableCell>
                      <ProdutoCargaPicker
                        value={p.produtoId || undefined}
                        descricao={p.descricao}
                        onPick={(prod) => pickProduto(p._key, prod)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input type="number" value={p.quantidade} className="w-24 text-right"
                        onChange={(e) => updateNumero(p._key, 'quantidade', e.target.value)} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input type="number" value={p.valorUnitario} className="w-28 text-right"
                        onChange={(e) => updateNumero(p._key, 'valorUnitario', e.target.value)} />
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {brl(p.quantidade * p.valorUnitario)}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={() => removeLinha(p._key)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} className="text-right font-medium text-muted-foreground">Total da Carga:</TableCell>
                  <TableCell className="text-right font-bold">{brl(totalCarga)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/operacional/cargas')}>Cancelar</Button>
        <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4" />Iniciar Carga
        </Button>
      </div>
    </div>
  );
}

// ── RetornoDialog ─────────────────────────────────────────────────────────

interface RetornoDialogProps {
  cargaId:  number;
  produtos: CargaProduto[];
  open:     boolean;
  onClose:  () => void;
}

interface LinhaRetorno {
  produtoId:         number;
  quantidadeSaida:   number;
  quantidadeRetorno: number;
}

function RetornoDialog({ cargaId, produtos, open, onClose }: RetornoDialogProps) {
  const queryClient = useQueryClient();
  const [dtRetorno, setDtRetorno] = useState(() => new Date().toISOString().slice(0, 10));
  const [linhas, setLinhas] = useState<LinhaRetorno[]>([]);

  // Ao abrir, pré-popula com os produtos da carga (igual ao desktop: edita só o retorno).
  useEffect(() => {
    if (open) {
      setLinhas(produtos.map((p) => ({
        produtoId:         p.produtoId,
        quantidadeSaida:   p.quantidade,
        quantidadeRetorno: p.quantidadeRetorno ?? 0,
      })));
      setDtRetorno(new Date().toISOString().slice(0, 10));
    }
  }, [open, produtos]);

  const updateRetorno = (produtoId: number, value: string) =>
    setLinhas((p) => p.map((r) => r.produtoId === produtoId ? { ...r, quantidadeRetorno: Number(value) } : r));

  const mutation = useMutation({
    mutationFn: () => {
      const request: RetornarCargaRequest = {
        dtRetorno: new Date(dtRetorno).toISOString(),
        produtos: linhas.map(({ produtoId, quantidadeRetorno }) => ({ produtoId, quantidadeRetorno })),
      };
      return cargaService.registrarRetorno(cargaId, request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carga-detalhe', String(cargaId)] });
      queryClient.invalidateQueries({ queryKey: ['carga'] });
      toast.success('Retorno registrado com sucesso');
      onClose();
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Registrar Retorno da Carga</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2 max-w-[12rem]">
            <Label>Data de Retorno</Label>
            <Input type="date" value={dtRetorno} onChange={(e) => setDtRetorno(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Produtos Retornados</Label>
            {linhas.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">Esta carga não possui produtos.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead className="text-right">Qtd. Saída</TableHead>
                    <TableHead className="text-right">Qtd. Retorno</TableHead>
                    <TableHead className="text-right">Divergência</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linhas.map((l) => {
                    const divergencia = l.quantidadeSaida - l.quantidadeRetorno;
                    return (
                      <TableRow key={l.produtoId}>
                        <TableCell className="font-medium">Produto #{l.produtoId}</TableCell>
                        <TableCell className="text-right">{l.quantidadeSaida}</TableCell>
                        <TableCell className="text-right">
                          <Input type="number" value={l.quantidadeRetorno} className="w-24 text-right ml-auto"
                            onChange={(e) => updateRetorno(l.produtoId, e.target.value)} />
                        </TableCell>
                        <TableCell className={`text-right ${divergencia !== 0 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                          {divergencia}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            <p className="text-xs text-muted-foreground">
              A divergência (saída − retorno) representa o que foi vendido em rota.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || linhas.length === 0} className="gap-2">
            <Save className="h-4 w-4" />Registrar Retorno
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── CargaDetalheView ──────────────────────────────────────────────────────

function CargaDetalheView() {
  const navigate    = useNavigate();
  const { id }      = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [retornoOpen, setRetornoOpen] = useState(false);

  const { data: carga, isLoading } = useQuery({
    queryKey: ['carga-detalhe', id],
    queryFn: () => cargaService.getById(Number(id)),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: AtualizarCargaRequest) => cargaService.update(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carga-detalhe', id] });
      queryClient.invalidateQueries({ queryKey: ['carga'] });
      toast.success('Carga atualizada');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  if (isLoading) {
    return <div className="p-6"><div className="text-center py-12 text-muted-foreground">Carregando...</div></div>;
  }

  if (!carga) return null;

  const status = calcularStatusCarga(carga);

  const statusBadgeClass: Record<string, string> = {
    montagem:   'bg-blue-500/10 text-blue-500',
    em_rota:    'bg-orange-500/10 text-orange-500',
    retornada:  'bg-purple-500/10 text-purple-500',
    finalizada: 'bg-green-500/10 text-green-500',
  };

  const statusLabel: Record<string, string> = {
    montagem: 'Montagem', em_rota: 'Em Rota', retornada: 'Retornada', finalizada: 'Finalizada',
  };

  return (
    <div className="p-6 space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/cargas')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Carga #{carga.numeroCarga}</h1>
            {carga.motorista && (
              <p className="text-muted-foreground text-sm mt-0.5 flex items-center gap-1">
                <MapPin className="h-3 w-3" />{carga.motorista}
              </p>
            )}
          </div>
          <Badge className={statusBadgeClass[status]}>{statusLabel[status]}</Badge>
        </div>
        <div className="flex gap-2 flex-wrap">
          {status === 'montagem' && (
            <Button variant="outline" className="gap-2"
              onClick={() => updateMutation.mutate({ dtSaida: new Date().toISOString() })}>
              <Truck className="h-4 w-4" />Registrar Saída
            </Button>
          )}
          {status === 'em_rota' && (
            <Button variant="outline" className="gap-2" onClick={() => setRetornoOpen(true)}>
              <RotateCcw className="h-4 w-4" />Registrar Retorno
            </Button>
          )}
          {status === 'retornada' && !carga.isConferido && (
            <Button variant="outline" className="gap-2"
              onClick={() => updateMutation.mutate({ isConferido: true })}>
              <CheckCircle2 className="h-4 w-4" />Marcar Conferido
            </Button>
          )}
          {(status === 'retornada' || status === 'em_rota') && !carga.isFinalizado && (
            <Button className="gap-2 bg-green-600 hover:bg-green-700"
              onClick={() => updateMutation.mutate({ isFinalizado: true })}
              disabled={updateMutation.isPending}>
              <CheckCircle2 className="h-4 w-4" />Finalizar Carga
            </Button>
          )}
        </div>
      </div>

      {/* Info */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Data Montagem</p>
              <p className="font-medium">{new Date(carga.dtMontagem).toLocaleDateString('pt-BR')}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Data Saída</p>
              <p className="font-medium">{carga.dtSaida ? new Date(carga.dtSaida).toLocaleDateString('pt-BR') : '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Data Retorno</p>
              <p className="font-medium">{carga.dtRetorno ? new Date(carga.dtRetorno).toLocaleDateString('pt-BR') : '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">KM Saída / Retorno</p>
              <p className="font-medium">{carga.kmSaida} / {carga.kmRetorno}</p>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Total Carga</p>
              <p className="font-bold">{brl(carga.valorTotalCarga)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Total Conferido</p>
              <p className="font-medium">{brl(carga.valorTotalConferido)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Qtd. Saída / Retorno</p>
              <p className="font-medium">{carga.qtdTotalSaida} / {carga.qtdTotalRetorno}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Divergência</p>
              <p className={`font-medium ${carga.valorDivergencia !== 0 ? 'text-destructive' : ''}`}>
                {brl(carga.valorDivergencia)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Produtos */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <PackageCheck className="h-5 w-5" />Produtos da Carga
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!carga.produtos || carga.produtos.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Nenhum produto nesta carga</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto ID</TableHead>
                  <TableHead>Sub-Produto ID</TableHead>
                  <TableHead className="text-right">Qtd. Saída</TableHead>
                  <TableHead className="text-right">Qtd. Retorno</TableHead>
                  <TableHead className="text-right">Divergente</TableHead>
                  <TableHead className="text-right">Valor Unit.</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Conferido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {carga.produtos.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.produtoId}</TableCell>
                    <TableCell>{p.subProdutoId}</TableCell>
                    <TableCell className="text-right">{p.quantidade}</TableCell>
                    <TableCell className="text-right">{p.quantidadeRetorno}</TableCell>
                    <TableCell className="text-right">
                      {p.quantidadeDivergente !== 0
                        ? <span className="text-destructive">{p.quantidadeDivergente}</span>
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right">{brl(p.valorUnitario)}</TableCell>
                    <TableCell className="text-right font-medium">{brl(p.valorTotal)}</TableCell>
                    <TableCell>
                      {p.isConferido
                        ? <Badge className="bg-green-500/10 text-green-500">Sim</Badge>
                        : <Badge className="bg-muted text-muted-foreground">Não</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <RetornoDialog
        cargaId={carga.id}
        produtos={carga.produtos ?? []}
        open={retornoOpen}
        onClose={() => setRetornoOpen(false)}
      />
    </div>
  );
}

// ── Export ────────────────────────────────────────────────────────────────

const CargaForm = () => {
  const { id } = useParams<{ id: string }>();
  return id && id !== 'nova' ? <CargaDetalheView /> : <NovaCargaForm />;
};

export default CargaForm;
